import type {AnyObject} from 'typings/portaro.fe.types';

export const MAX_JAVA_INT = 0x7fffffff;

/**
 * @module custom-utils
 * @name identifiedValuesEquals
 * @kind function
 *
 * @param {object} iv1 Identified value 1
 * @param {object} iv2 Identified value 2
 *
 * @return {boolean}
 *
 * @description
 * Compares two identified values
 */
export function identifiedValuesEquals(iv1, iv2) {
    if (isUndefined(iv1) || iv1 === null || isUndefined(iv2) || iv2 === null) {
        return false;
    }

    if (iv1 === iv2) {
        return true;
    }

    let val1 = iv1;
    if (isDefined(iv1.id)) {
        val1 = iv1.id;
    }

    let val2 = iv2;
    if (isDefined(iv2.id)) {
        val2 = iv2.id;
    }

    return val1 === val2;
}

/**
 * @module custom-utils
 * @name hash
 * @kind function
 *
 * @param {string} s input
 * @returns {string} computed hash
 *
 * @description
 * Compute simple hash
 */
export function hash(s: string) {
    /* Simple hash function. */
    let a = 1;
    let c = 0;
    let h;
    let o;

    if (s) {
        a = 0;
        /* eslint-disable no-bitwise */
        for (h = s.length - 1; h >= 0; h--) {
            o = s.charCodeAt(h);
            // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
            a = (a << 6 & 268435455) + o + (o << 14);
            c = a & 266338304;
            a = c !== 0 ? a ^ c >> 21 : a;
        }
        /* eslint-enable no-bitwise */
    }
    return String(a);
}

export interface Thenable<T> {
    then: (...args: any[]) => T;
}

/**
 * @module custom-utils
 * @name isThenable
 * @kind function
 *
 * @param value Tested value
 * @returns {boolean} True -> Is thenable object (= have function `then`)
 *
 * @description
 * Checks if value is thenable
 */
export function isThenable<T>(value): value is Thenable<T> {
    return value && typeof value.then === 'function';
}

/**
 * @module custom-utils
 * @name isPromise
 * @kind function
 *
 * @param value Tested value
 * @returns {boolean} True -> Is Promise
 *
 * @description
 * Checks if value is Promise (= has `then` and `catch` functions).
 */
export function isPromise<T>(value): value is Promise<T> {
    return value &&
        typeof value.then === 'function' &&
        typeof value.catch === 'function';
}

/**
 * @module custom-utils
 * @name deepFreeze
 * @kind function
 *
 * @template T
 *
 * @param {T} object Object to freeze
 * @returns {T} Frozen object
 *
 * @description
 * Performs deep freeze on given object
 */
export function deepFreeze<T extends Record<string, any>>(object: T): T {
    Object.freeze(object);

    Object.getOwnPropertyNames(object).forEach((prop) => {
        if (Object.hasOwn(object, prop)
            && object[prop] !== null
            && (typeof object[prop] === 'object' || typeof object[prop] === 'function')
            && !Object.isFrozen(object[prop])) {

            deepFreeze(object[prop]);
        }
    });

    return object;
}

/**
 * @module custom-utils
 * @name uuid
 * @kind function
 *
 * @returns {string} GUID
 *
 * @description
 * Returns UUID v4
 */
export function uuid() {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
        // eslint-disable-next-line
        const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
        return v.toString(16);
    });
}

/**
 * @module custom-utils
 * @name isFunction
 * @kind function
 *
 * @param functionToCheck
 *
 * @returns {functionToCheck is (...args: any[]) => any}
 *
 * @description
 * Checks if argument is function.
 */
export function isFunction(functionToCheck): functionToCheck is (...args: any[]) => any {
    return functionToCheck && {}.toString.call(functionToCheck) === '[object Function]';
}

/**
 * @module custom-utils
 * @name Toggler
 * @kind class
 *
 * @description
 * Class encapsulates toggle mechanism with 2 inner states.
 * Constructor defines the states. First state is saved as initial.
 * Toggle method toggles between states. Use getState to get current state.
 */
export class Toggler<T> {
    private currentState: T;

    constructor(private initialState: T, private otherState: T) {
        this.currentState = initialState;
    }

    public toggle() {
        if(this.currentState === this.initialState) {
            this.currentState = this.otherState;
        } else {
            this.currentState = this.initialState;
        }
    }

    public getState(): T {
        return this.currentState;
    }

    public reset(): void {
        this.currentState = this.initialState;
    }
}

/**
 * @module custom-utils
 * @name isEmptyObject
 * @kind function
 *
 * @param {object} value
 *
 * @returns {boolean} true if is empty
 *
 * @description
 * Checks if object is equal to {} (has no properties)
 */
// eslint-disable-next-line @typescript-eslint/ban-types
export function isEmptyObject(value: object): boolean {
    return Object.keys(value).length === 0 // no props
        && value.constructor === Object; // for primitive values wrapper classes (new Number())
}

/**
 * @module custom-utils
 * @param {{}} object
 * @param {string[]} keys
 * @returns {{}}
 * @description
 * Filter object keys
 */
export function filterObjectKeys(object: AnyObject, ...keys: string[]): AnyObject {
    return Object.keys(object)
                 .filter((key) => keys.includes(key))
                 .reduce((res, key) => {
                     res[key] = object[key];
                     return res;
                 }, {});
}

export function isDefined(value: unknown): boolean {
    return typeof value !== 'undefined';
}

export function isNull(value: unknown): boolean {
    return value === null;
}

export function isNullOrUndefined(value: unknown): boolean {
    return isNull(value) || isUndefined(value);
}

export function isUndefined(value: unknown): boolean {
    return typeof value === 'undefined';
}

export function exists(value: unknown): boolean {
    return !isNull(value) && !isUndefined(value);
}

// https://dev.to/ddiprose/exhaustive-switch-statement-with-typescript-26dh
export function assertUnreachable(value: never): never {
    throw new Error(`ERROR! Reached forbidden guard function with unexpected value: ${JSON.stringify(value)}`);
}
